import { useState, type FormEvent } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, CheckCircle } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { Button } from "../../components/ui/Button";

interface PasswordStrength {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
}

function checkStrength(password: string): PasswordStrength {
  return {
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };
}

function StrengthRule({ met, label }: { met: boolean; label: string }) {
  return (
    <li className={`flex items-center gap-1.5 text-xs ${met ? "text-[#16a34a]" : "text-[#94a3b8]"}`}>
      <CheckCircle className={`h-3.5 w-3.5 ${met ? "text-[#16a34a]" : "text-[#e2e8f0]"}`} />
      {label}
    </li>
  );
}

export default function ChangePasswordPage() {
  const { changePassword, needsPasswordChange } = useAuth();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If not in password-change flow, redirect away
  if (!needsPasswordChange) {
    return <Navigate to="/login" replace />;
  }

  const strength = checkStrength(newPassword);
  const isStrong = Object.values(strength).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = isStrong && passwordsMatch;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await changePassword(newPassword);
      // Auth hook will update user; navigate after
      navigate("/employee/dashboard", { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to change password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#eff3ff] via-[#f8fafc] to-[#dbe4ff] p-4">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="mb-8 flex flex-col items-center gap-2">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#2447d9] shadow-lg">
            <span className="text-2xl font-extrabold text-white">M</span>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-extrabold text-[#2447d9]">MGC</h1>
            <p className="text-sm text-[#64748b]">Hub</p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white border border-[#e2e8f0] shadow-[0_4px_16px_0_rgb(0_0_0/0.08)] p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#eff3ff]">
              <KeyRound className="h-5 w-5 text-[#2447d9]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#0f172a]">Set Your New Password</h2>
              <p className="text-xs text-[#94a3b8]">First-time login password change required</p>
            </div>
          </div>

          <p className="mb-5 text-sm text-[#64748b] bg-[#eff3ff] rounded-lg px-4 py-3">
            Your account was created with a temporary password. Please set a new, secure password to continue.
          </p>

          {error && (
            <div className="mb-4 rounded-lg bg-[#fef2f2] border border-[#dc2626]/20 px-4 py-3 text-sm text-[#dc2626]">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* New Password */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-password" className="text-sm font-medium text-[#0f172a]">
                New Password <span className="text-[#dc2626]">*</span>
              </label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 pr-10 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b]"
                  aria-label={showNew ? "Hide password" : "Show password"}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {/* Strength rules */}
              {newPassword.length > 0 && (
                <ul className="mt-1.5 space-y-1">
                  <StrengthRule met={strength.hasMinLength} label="At least 8 characters" />
                  <StrengthRule met={strength.hasUppercase} label="At least one uppercase letter" />
                  <StrengthRule met={strength.hasLowercase} label="At least one lowercase letter" />
                  <StrengthRule met={strength.hasNumber} label="At least one number" />
                </ul>
              )}
            </div>

            {/* Confirm Password */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirm-password" className="text-sm font-medium text-[#0f172a]">
                Confirm Password <span className="text-[#dc2626]">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 pr-10 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-transparent transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b]"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword.length > 0 && (
                <p
                  className={`text-xs ${passwordsMatch ? "text-[#16a34a]" : "text-[#dc2626]"}`}
                >
                  {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                </p>
              )}
            </div>

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={isSubmitting}
              disabled={!canSubmit}
            >
              Set New Password
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
